"use client";

import { useEffect } from "react";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

interface WebVitalsReport {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
}

const WebVitals = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Function to handle Web Vitals reporting
    const reportWebVitals = (metric: WebVitalsReport) => {
      const { name, value, rating, delta, id } = metric;

      // Console logging for development
      if (process.env.NODE_ENV === "development") {
        console.group(`📊 Web Vital: ${name}`);
        console.log(
          "Value:",
          name === "CLS" ? value.toFixed(4) : `${value.toFixed(2)}ms`
        );
        console.log("Rating:", rating);
        console.log(
          "Delta:",
          name === "CLS" ? delta.toFixed(4) : `${delta.toFixed(2)}ms`
        );
        console.log("ID:", id);
        console.groupEnd();

        // Color-coded console output based on rating
        const colors = {
          good: "color: #22c55e; font-weight: bold",
          "needs-improvement": "color: #f59e0b; font-weight: bold",
          poor: "color: #ef4444; font-weight: bold",
        };

        const displayValue =
          name === "CLS" ? value.toFixed(4) : `${value.toFixed(2)}ms`;
        console.log(`%c${name}: ${displayValue} (${rating})`, colors[rating]);

        // Performance warnings
        if (rating === "poor") {
          console.warn(`⚠️ ${name} needs improvement: ${displayValue}`);
        }
      }

      // Send to analytics in production
      if (process.env.NODE_ENV === "production") {
        // Send to API endpoint for server-side processing
        try {
          fetch("/api/web-vitals", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...metric,
              timestamp: Date.now(),
              url: window.location.href,
              userAgent: navigator.userAgent,
            }),
          }).catch((error) => {
            console.warn("Failed to send Web Vital to API:", error);
          });
        } catch (error) {
          console.warn("Failed to send Web Vital to API:", error);
        }

        // You can also send to Google Analytics, your own analytics, etc.
        // Example for Google Analytics 4:
        // if (typeof gtag !== 'undefined') {
        //   gtag('event', 'web_vital', {
        //     event_category: 'Web Vitals',
        //     event_label: name,
        //     value: Math.round(name === 'CLS' ? value * 1000 : value),
        //     custom_map: { metric_rating: rating }
        //   });
        // }

        // For now, we'll also log to console in production
        const displayValue =
          name === "CLS" ? value.toFixed(4) : `${value.toFixed(2)}ms`;
        console.log(`Web Vital - ${name}: ${displayValue} (${rating})`);
      }

      // Store in localStorage for historical tracking
      try {
        const stored = localStorage.getItem("web-vitals-history") || "[]";
        const history = JSON.parse(stored);
        history.push({
          ...metric,
          timestamp: Date.now(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        });

        // Keep only last 100 entries
        if (history.length > 100) {
          history.splice(0, history.length - 100);
        }

        localStorage.setItem("web-vitals-history", JSON.stringify(history));
      } catch (error) {
        console.warn("Failed to store Web Vitals history:", error);
      }
    };

    // Initialize Web Vitals measurement
    onCLS(reportWebVitals);
    onFCP(reportWebVitals);
    onINP(reportWebVitals); // INP replaced FID in web-vitals v3+
    onLCP(reportWebVitals);
    onTTFB(reportWebVitals);

    // Additional performance monitoring
    const measurePageLoad = () => {
      if (document.readyState === "complete") {
        const navigation = performance.getEntriesByType(
          "navigation"
        )[0] as PerformanceNavigationTiming;

        if (navigation) {
          const loadTime = navigation.loadEventEnd - navigation.fetchStart;
          const domContentLoaded =
            navigation.domContentLoadedEventEnd - navigation.fetchStart;
          const timeToFirstByte =
            navigation.responseStart - navigation.requestStart;

          console.group("📈 Page Load Metrics");
          console.log("Total Load Time:", `${loadTime.toFixed(2)}ms`);
          console.log(
            "DOM Content Loaded:",
            `${domContentLoaded.toFixed(2)}ms`
          );
          console.log("Time to First Byte:", `${timeToFirstByte.toFixed(2)}ms`);
          console.groupEnd();

          // Performance warnings
          if (loadTime > 3000) {
            console.warn(
              "⚠️ Page load time is over 3 seconds. Consider optimizing."
            );
          }
          if (timeToFirstByte > 600) {
            console.warn(
              "⚠️ TTFB is over 600ms. Consider server optimization."
            );
          }
        }
      }
    };

    // Measure page load when ready
    if (document.readyState === "complete") {
      measurePageLoad();
    } else {
      window.addEventListener("load", measurePageLoad);
    }

    // Resource timing analysis
    const analyzeResources = () => {
      const resources = performance.getEntriesByType("resource");
      const largeResources = resources.filter(
        (resource) => resource.transferSize > 100000
      ); // >100KB

      if (largeResources.length > 0) {
        console.group("📦 Large Resources (>100KB)");
        largeResources.forEach((resource) => {
          console.log(
            `${resource.name}: ${(resource.transferSize / 1024).toFixed(2)}KB`
          );
        });
        console.groupEnd();
      }
    };

    // Analyze resources after load
    setTimeout(analyzeResources, 2000);

    // Cleanup
    return () => {
      window.removeEventListener("load", measurePageLoad);
    };
  }, []);

  return null;
};

export default WebVitals;
